// @ts-nocheck
import '../components/posts-panel/posts-panel.css';
import { api } from '../api/client';
import { showView, getViewContainer, getCurrentView, onViewChange } from './view-router';
import {
  getHasHeadstones,
  getMyHeadstones,
  getPostQuota,
  onPostsAccessChange,
  refreshPostsAccess,
  type CommentItem,
  type PostItem,
} from './posts-access';
import { getCurrentUser } from '../auth/session';
import { t } from '../i18n';
import { translateApiMessage } from '../i18n/api-errors';
import { navigateToHeadstone } from '../map/headstone-nav';
import { goHome } from '../map/camera';
import { mapState } from '../map/state';
import { showToast } from '../components/toast/toast';
import {
  attachPetPhotoFallbacks,
  renderPetPhotoHtml,
} from '../utils/pet-photo-placeholder';
import {
  bindVisualViewportInsets,
  unbindVisualViewportInsets,
} from '../utils/viewport';
import { feedAdItemHtml, fillAdsenseUnits } from './ads';

declare const lucide: { createIcons: () => void } | undefined;

const COMMUNITY_VIEW_VERSION = 4;
const POST_SHORT_CONTENT_THRESHOLD = 80;
let communityViewMounted = false;
let mountedCommunityViewVersion = 0;

function refreshCommunityIcons(): void {
  if (typeof lucide !== 'undefined') lucide.createIcons();
}

function isMobileCommunity(): boolean {
  return window.matchMedia('(max-width: 768px)').matches;
}

function ensureCommunityView(): void {
  const host = getViewContainer('community');
  if (!host) return;

  const hasCurrentShell =
    communityViewMounted &&
    mountedCommunityViewVersion === COMMUNITY_VIEW_VERSION &&
    !!host.querySelector('#posts-list');

  if (hasCurrentShell) return;

  host.innerHTML = /* html */ `
    <div class="community-body">
      <section class="community-threads" aria-label="${t('posts.communityTitle')}">
        <header class="community-threads__head">
          <h1 class="community-threads__title">${t('posts.communityTitle')}</h1>
          <p class="community-threads__status">
            <span class="status-dot" aria-hidden="true"></span>
            <span id="community-presence">${t('posts.presence')}</span>
          </p>
        </header>
        <div id="posts-list" class="community-threads__list pscroll" role="listbox" aria-label="${t('posts.feed')}"></div>
        <button id="posts-load-more" type="button" class="community-threads__load-more" hidden>${t('posts.loadMore')}</button>
        <div class="community-compose community-compose--threads" id="community-new-compose">
          <p id="posts-guest-notice" class="posts-guest-notice" hidden>${t('posts.guestNotice')}</p>
          <p id="posts-quota-exhausted-hint" class="posts-quota-exhausted-hint" hidden></p>
          <div id="posts-create-extras" class="posts-create-extras" hidden>
            <label for="posts-headstone-select">${t('posts.memorial')}</label>
            <select id="posts-headstone-select" name="headstoneId" form="posts-create-form"></select>
            <p id="posts-quota-hint" class="posts-quota-hint"></p>
          </div>
          <form class="compose-pill compose-pill--new" id="posts-create-form">
            <textarea
              id="posts-content-input"
              name="content"
              rows="2"
              maxlength="1000"
              placeholder="${t('posts.newMessage')}"
              autocomplete="off"
            ></textarea>
            <button type="submit" class="compose-pill__send" aria-label="${t('posts.publish')}">
              <i data-lucide="send"></i>
            </button>
          </form>
          <p id="posts-create-error" class="posts-error" hidden></p>
        </div>
      </section>

      <section class="community-thread-panel" id="community-thread-panel" aria-label="${t('posts.discussion')}">
        <div class="community-thread-panel__empty" id="community-thread-empty">
          <p class="community-thread-panel__empty-title">${t('posts.selectDiscussion')}</p>
          <p class="community-thread-panel__empty-hint">${t('posts.selectDiscussionHint')}</p>
        </div>
        <div class="community-thread-panel__content" id="community-thread-content" hidden>
          <header class="community-thread-panel__head">
            <button type="button" class="community-thread-panel__back" id="community-thread-back" aria-label="${t('posts.backToList')}">
              <i data-lucide="arrow-left"></i>
            </button>
            <h2 class="community-thread-panel__title">${t('posts.discussion')}</h2>
          </header>
          <div id="thread-messages" class="community-thread-panel__messages pscroll" aria-live="polite"></div>
          <div class="community-compose community-compose--reply" id="community-reply-compose"></div>
        </div>
      </section>
    </div>
  `;

  communityViewMounted = true;
  mountedCommunityViewVersion = COMMUNITY_VIEW_VERSION;
  bindThreadMessageActions();
  refreshCommunityIcons();
}

let nextCursor: string | null = null;
let loadingPosts = false;
let feedRequestId = 0;
let selectedPostId: string | null = null;
let pendingSelectPostId: string | null = null;
const postsCache = new Map<string, PostItem>();
const commentsCache = new Map<string, CommentItem[]>();

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text ?? '';
  return div.innerHTML;
}

function formatAuthorLabel(emailMasked?: string | null): string {
  const raw = (emailMasked ?? '').trim();
  if (!raw) return t('posts.anonymousUser');

  const local = raw.split('@')[0] ?? raw;
  const visible = local.replace(/\*+/g, '').trim();
  if (visible.length >= 2) {
    return visible.charAt(0).toUpperCase() + visible.slice(1).toLowerCase();
  }

  const letters = local.replace(/[^a-zA-ZàèéìòùÀÈÉÌÒÙ]/g, '');
  if (letters.length >= 1) {
    return letters.charAt(0).toUpperCase() + letters.slice(1).toLowerCase();
  }

  return t('posts.anonymousUser');
}

function replyLabel(count: number): string {
  if (count <= 0) return t('posts.reply');
  if (count === 1) return t('posts.replySingular');
  return t('posts.repliesCount', { count });
}

function replyHeading(count: number): string {
  if (count === 1) return t('posts.replySingular');
  return t('posts.repliesCount', { count });
}

const POST_MUTATION_WINDOW_MS = 24 * 60 * 60 * 1000;

function isWithin24h(isoDate: string): boolean {
  const created = new Date(isoDate).getTime();
  if (Number.isNaN(created)) return false;
  return Date.now() - created <= POST_MUTATION_WINDOW_MS;
}

function renderBubbleActions(options: {
  postId: string;
  commentId?: string;
}): string {
  const editLabel = escapeHtml(t('posts.edit'));
  const deleteLabel = escapeHtml(t('posts.delete'));

  if (options.commentId) {
    return `
      <div class="thread-bubble-actions">
        <button
          type="button"
          class="thread-bubble-action"
          data-edit-comment="${escapeHtml(options.commentId)}"
          data-post-id="${escapeHtml(options.postId)}"
          aria-label="${editLabel}"
        >${editLabel}</button>
        <button
          type="button"
          class="thread-bubble-action"
          data-delete-comment="${escapeHtml(options.commentId)}"
          data-post-id="${escapeHtml(options.postId)}"
          aria-label="${deleteLabel}"
        >${deleteLabel}</button>
      </div>
    `;
  }

  return `
    <div class="thread-bubble-actions">
      <button
        type="button"
        class="thread-bubble-action"
        data-edit-post="${escapeHtml(options.postId)}"
        aria-label="${editLabel}"
      >${editLabel}</button>
      <button
        type="button"
        class="thread-bubble-action"
        data-delete-post="${escapeHtml(options.postId)}"
        aria-label="${deleteLabel}"
      >${deleteLabel}</button>
    </div>
  `;
}

function renderEditForm(options: {
  kind: 'post' | 'comment';
  postId: string;
  commentId?: string;
  content: string;
  maxLength: number;
  mine?: boolean;
}): string {
  const saveLabel = escapeHtml(t('posts.save'));
  const cancelLabel = escapeHtml(t('posts.cancel'));

  return `
    <form
      class="message-bubble message-bubble--editing${options.mine ? ' is-mine' : ''}"
      data-edit-form="${options.kind}"
      data-post-id="${escapeHtml(options.postId)}"
      ${options.commentId ? `data-comment-id="${escapeHtml(options.commentId)}"` : ''}
    >
      <textarea name="content" maxlength="${options.maxLength}" required>${escapeHtml(options.content)}</textarea>
      <div class="thread-bubble-edit-actions">
        <button type="button" data-cancel-edit>${cancelLabel}</button>
        <button type="submit">${saveLabel}</button>
      </div>
    </form>
  `;
}

function updatePostExcerptInFeed(postId: string, content: string): void {
  const excerpt = document.querySelector(
    `.thread-item[data-thread-id="${postId}"] .thread-item__excerpt`,
  );
  if (excerpt) excerpt.textContent = content;
}

function memorialBadgeHtml(petName: string): string {
  return `
    <span class="thread-item__mem">
      <i data-lucide="paw-print" aria-hidden="true"></i>
      ${escapeHtml(petName)}
    </span>
  `;
}

function renderMemorialShareCard(post: PostItem): string {
  const pet = post.headstone?.pet;
  const headstoneId = post.headstoneId;
  if (!pet?.name || !headstoneId) return '';

  const photo = renderPetPhotoHtml({
    variant: 'avatar',
    photoUrl: pet.imageUrl,
    seed: pet.id ?? headstoneId,
    name: pet.name,
    className: 'thread-post-card__share-photo',
    alt: pet.name,
  });

  return `
    <button type="button" class="thread-post-card__share" data-open-memorial="${escapeHtml(headstoneId)}">
      ${photo}
      <span class="thread-post-card__share-name">${escapeHtml(pet.name)}</span>
    </button>
  `;
}

function renderThreadItem(post: PostItem): string {
  const pet = post.headstone?.pet;
  const count = post.commentCount ?? 0;
  const active = selectedPostId === post.id;
  postsCache.set(post.id, post);

  return `
    <button
      type="button"
      class="thread-item${active ? ' is-active' : ''}"
      data-thread-id="${escapeHtml(post.id)}"
      role="option"
      aria-selected="${active ? 'true' : 'false'}"
    >
      <div class="thread-item__top">
        <div class="thread-item__who-row">
          <span class="thread-item__who">${escapeHtml(formatAuthorLabel(post.author?.emailMasked))}</span>
          ${pet?.name ? memorialBadgeHtml(pet.name) : ''}
        </div>
        <span class="thread-item__reply">${replyLabel(count)}</span>
      </div>
      <p class="thread-item__excerpt">${escapeHtml(post.content)}</p>
    </button>
  `;
}

function renderThreadMessages(post: PostItem, comments: CommentItem[]): string {
  const userId = getCurrentUser()?.id;
  const count = comments.length;
  const authorLabel = escapeHtml(formatAuthorLabel(post.author?.emailMasked));
  const canManagePost = !!userId && post.authorId === userId && isWithin24h(post.createdAt);
  const postHead = canManagePost
    ? `
      <div class="thread-bubble-group__head">
        <span class="thread-bubble-group__author">${authorLabel}</span>
        ${renderBubbleActions({ postId: post.id })}
      </div>
    `
    : `<span class="thread-bubble-group__author">${authorLabel}</span>`;

  let html = `
    <div class="thread-messages-inner">
      <div class="thread-bubble-group">
        ${postHead}
        <div class="message-bubble thread-post-bubble" data-post-bubble="${escapeHtml(post.id)}">${escapeHtml(post.content)}</div>
      </div>
      ${renderMemorialShareCard(post)}
  `;

  if (count > 0) {
    html += `<p class="thread-replies-separator">${replyHeading(count)}</p>`;
    html += `<div class="thread-bubble-stack">`;

    comments.forEach((comment) => {
      const mine = !!userId && comment.authorId === userId;
      const who = mine
        ? escapeHtml(t('posts.you'))
        : escapeHtml(formatAuthorLabel(comment.author?.emailMasked ?? comment.pet?.name));
      const commentHead = mine
        ? `
          <div class="thread-bubble-group__head">
            <span class="thread-bubble-group__author">${who}</span>
            ${renderBubbleActions({ postId: post.id, commentId: comment.id })}
          </div>
        `
        : `<span class="thread-bubble-group__author">${who}</span>`;

      html += `
        <div class="thread-bubble-group${mine ? ' thread-bubble-group--mine' : ''}">
          ${commentHead}
          <div class="message-bubble${mine ? ' is-mine' : ''}" data-comment-bubble="${escapeHtml(comment.id)}">${escapeHtml(comment.content)}</div>
        </div>
      `;
    });

    html += `</div>`;
  }

  html += `</div>`;
  return html;
}

function hasAvailablePostQuota(): boolean {
  return getHasHeadstones() && getPostQuota().some((q) => q.canPostThisWeek);
}

function updateComposeContext(): void {
  const compose = document.getElementById('community-new-compose');
  const input = document.getElementById('posts-content-input') as HTMLTextAreaElement | null;
  const quotaHint = document.getElementById('posts-quota-exhausted-hint');
  const quotaAvailable = hasAvailablePostQuota();
  const hasDiscussion = !!selectedPostId;

  if (compose) {
    compose.classList.toggle('community-compose--secondary', hasDiscussion && !quotaAvailable);
    compose.classList.toggle('community-compose--discussion-open', hasDiscussion);
  }

  if (quotaHint) {
    if (!quotaAvailable && getHasHeadstones()) {
      quotaHint.textContent = hasDiscussion
        ? t('posts.quotaExhausted')
        : t('posts.quotaExhaustedNoDiscussion');
      quotaHint.hidden = false;
    } else {
      quotaHint.hidden = true;
      quotaHint.textContent = '';
    }
  }

  if (input) {
    input.placeholder = hasDiscussion
      ? t('posts.newMessageWithDiscussion')
      : t('posts.newMessage');
  }
}

function focusReplyInput(): void {
  if (!selectedPostId) return;
  const form = document.querySelector(
    `[data-comment-form="${selectedPostId}"]`,
  ) as HTMLFormElement | null;
  const input = form?.querySelector('[name="content"]') as HTMLTextAreaElement | null;
  if (input && !input.disabled) {
    input.focus();
  }
}

function renderReplyCompose(postId: string): string {
  const headstones = getMyHeadstones();
  if (!headstones.length) {
    return `
      <div class="community-reply-wrap community-reply-wrap--guest">
        <div class="compose-pill compose-pill--reply compose-pill--disabled">
          <textarea
            rows="2"
            placeholder="${t('posts.replyPlaceholder')}"
            disabled
            aria-disabled="true"
          ></textarea>
          <button type="button" class="compose-pill__send compose-pill__send--reply" disabled aria-hidden="true">
            <i data-lucide="send"></i>
          </button>
        </div>
        <p class="posts-guest-reply-hint">${t('posts.guestReplyHint')}</p>
      </div>
    `;
  }

  const showPetSelect = headstones.length > 1;
  const options = headstones
    .map(
      (hs) =>
        `<option value="${escapeHtml(hs.pet.id)}">${escapeHtml(hs.pet.name)}</option>`
    )
    .join('');

  return `
    <div class="community-reply-wrap">
      ${showPetSelect ? `
        <div class="community-reply-pet">
          <label for="pet-${escapeHtml(postId)}">${t('posts.yourPet')}</label>
          <select id="pet-${escapeHtml(postId)}" form="reply-form-${escapeHtml(postId)}" name="petId">${options}</select>
        </div>
      ` : ''}
      <form class="compose-pill compose-pill--reply" id="reply-form-${escapeHtml(postId)}" data-comment-form="${escapeHtml(postId)}">
        ${showPetSelect ? '' : `<input type="hidden" name="petId" value="${escapeHtml(headstones[0].pet.id)}" />`}
        <textarea
          name="content"
          rows="2"
          maxlength="500"
          placeholder="${t('posts.replyPlaceholder')}"
          autocomplete="off"
          required
        ></textarea>
        <button type="submit" class="compose-pill__send compose-pill__send--reply" aria-label="${t('posts.comment')}">
          <i data-lucide="send"></i>
        </button>
      </form>
      <p class="posts-error" data-comment-error="${escapeHtml(postId)}" hidden></p>
    </div>
  `;
}

function findLegacyHeadstoneById(headstoneId: string) {
  return Object.values(mapState.gravesData).find((entry) => entry?.id === headstoneId) ?? null;
}

function openPostMemorial(headstoneId: string): void {
  const legacy = findLegacyHeadstoneById(headstoneId);
  if (!legacy) {
    showToast(t('memorial.notFound'));
    return;
  }
  goHome();
  navigateToHeadstone(legacy);
}

function bindMemorialShareCards(root: ParentNode = document): void {
  root.querySelectorAll('[data-open-memorial]').forEach((btn) => {
    if (btn.dataset.bound) return;
    btn.dataset.bound = '1';
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-open-memorial');
      if (id) openPostMemorial(id);
    });
  });
}

function updateGuestNotice() {
  const form = document.getElementById('posts-create-form') as HTMLFormElement | null;
  const input = document.getElementById('posts-content-input') as HTMLTextAreaElement | null;
  const sendBtn = form?.querySelector('.compose-pill__send') as HTMLButtonElement | null;
  const notice = document.getElementById('posts-guest-notice');
  const extras = document.getElementById('posts-create-extras');
  if (!notice) return;

  const guest = !getHasHeadstones();
  notice.hidden = !guest;
  if (extras) extras.hidden = guest || getPostQuota().filter((q) => q.canPostThisWeek).length <= 1;
  if (input) input.disabled = guest;
  if (sendBtn) sendBtn.disabled = guest;
}

function updateCreateForm(preferredHeadstoneId?: string) {
  const select = document.getElementById('posts-headstone-select') as HTMLSelectElement | null;
  const hint = document.getElementById('posts-quota-hint');
  const extras = document.getElementById('posts-create-extras');
  const form = document.getElementById('posts-create-form') as HTMLFormElement | null;
  const input = document.getElementById('posts-content-input') as HTMLTextAreaElement | null;
  const sendBtn = form?.querySelector('.compose-pill__send') as HTMLButtonElement | null;
  if (!select || !hint || !extras || !form) return;

  const quota = getPostQuota().filter((q) => q.canPostThisWeek);
  select.innerHTML = '';

  if (!quota.length || !getHasHeadstones()) {
    extras.hidden = true;
    form.dataset.headstoneId = '';
    if (input) input.disabled = true;
    if (sendBtn) sendBtn.disabled = true;
    updateGuestNotice();
    updateComposeContext();
    return;
  }

  quota.forEach((q) => {
    const opt = document.createElement('option');
    opt.value = q.headstoneId;
    opt.textContent = q.petName;
    select.appendChild(opt);
  });

  if (preferredHeadstoneId) {
    const match = Array.from(select.options).find((opt) => opt.value === preferredHeadstoneId);
    if (match) select.value = preferredHeadstoneId;
  }

  extras.hidden = quota.length <= 1;
  form.dataset.headstoneId = quota.length === 1 ? quota[0].headstoneId : '';

  const unavailable = getPostQuota().filter((q) => !q.canPostThisWeek);
  hint.textContent = unavailable.length
    ? t('posts.quotaBlocked', { count: unavailable.length })
    : t('posts.quotaHint');
  hint.classList.remove('posts-error');

  if (input) input.disabled = false;
  if (sendBtn) sendBtn.disabled = false;
  updateGuestNotice();
  updateComposeContext();
}

function syncThreadPanelVisibility(): void {
  const empty = document.getElementById('community-thread-empty');
  const content = document.getElementById('community-thread-content');
  const panel = document.getElementById('community-thread-panel');
  const hasSelection = !!selectedPostId;

  if (empty) empty.hidden = hasSelection;
  if (content) content.hidden = !hasSelection;
  if (panel) {
    panel.classList.toggle('community-thread-panel--has-thread', hasSelection);
    if (isMobileCommunity()) {
      panel.classList.toggle('is-open', hasSelection);
    } else {
      panel.classList.remove('is-open');
    }
  }

  const discussionOpen =
    isMobileCommunity() && hasSelection && getCurrentView() === 'community';

  document.body.classList.toggle('discussion-sheet-open', discussionOpen);

  if (discussionOpen) {
    bindVisualViewportInsets();
  } else {
    unbindVisualViewportInsets();
  }

  updateComposeContext();
}

function updateThreadSelectionUi(): void {
  document.querySelectorAll('.thread-item').forEach((el) => {
    const id = el.getAttribute('data-thread-id');
    const active = id === selectedPostId;
    el.classList.toggle('is-active', active);
    el.setAttribute('aria-selected', active ? 'true' : 'false');
  });
  syncThreadPanelVisibility();
}

function updateThreadMeta(postId: string, count: number): void {
  const item = document.querySelector(`.thread-item[data-thread-id="${postId}"] .thread-item__reply`);
  if (item) item.textContent = replyLabel(count);

  const post = postsCache.get(postId);
  if (post) {
    post.commentCount = count;
    postsCache.set(postId, post);
  }
}

async function loadComments(postId: string): Promise<CommentItem[]> {
  const { data, error } = await api.GET('/posts/{id}/comments', {
    params: { path: { id: postId } },
  });

  if (error) throw error;

  const comments = (data ?? []) as CommentItem[];
  commentsCache.set(postId, comments);
  updateThreadMeta(postId, comments.length);
  return comments;
}

async function renderThreadPanel(postId: string): Promise<void> {
  const messages = document.getElementById('thread-messages');
  const replyCompose = document.getElementById('community-reply-compose');
  const post = postsCache.get(postId);
  if (!messages || !post) return;

  messages.innerHTML = `<p class="posts-empty">${t('posts.loadingComments')}</p>`;
  if (replyCompose) replyCompose.innerHTML = '';

  try {
    const comments = await loadComments(postId);
    if (selectedPostId !== postId) return;

    messages.innerHTML = renderThreadMessages(post, comments);
    if (replyCompose) replyCompose.innerHTML = renderReplyCompose(postId);
    bindCommentForms();
    bindMemorialShareCards(messages);
    attachPetPhotoFallbacks(messages);
    refreshCommunityIcons();
    updateComposeContext();
    if (!hasAvailablePostQuota()) {
      focusReplyInput();
    }
  } catch {
    if (selectedPostId !== postId) return;
    messages.innerHTML = `<p class="posts-error">${t('posts.commentsError')}</p>`;
  }
}

async function selectThread(postId: string | null, options: { scroll?: boolean } = {}): Promise<void> {
  selectedPostId = postId;
  updateThreadSelectionUi();

  if (!postId) return;

  await renderThreadPanel(postId);

  if (options.scroll) {
    const item = document.querySelector(`.thread-item[data-thread-id="${postId}"]`);
    item?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

async function deletePost(postId: string): Promise<void> {
  if (!window.confirm(t('posts.deleteConfirm'))) return;

  const { error } = await api.DELETE('/posts/{id}', {
    params: { path: { id: postId } },
  });

  if (error) {
    showToast(translateApiMessage(error.message) || t('posts.deleteError'));
    return;
  }

  postsCache.delete(postId);
  commentsCache.delete(postId);
  selectedPostId = null;
  showToast(t('posts.postDeleted'));
  syncThreadPanelVisibility();
  await refreshFeed(true);
}

async function deleteComment(postId: string, commentId: string): Promise<void> {
  if (!window.confirm(t('posts.deleteConfirm'))) return;

  const { error } = await api.DELETE('/posts/{id}/comments/{commentId}', {
    params: { path: { id: postId, commentId } },
  });

  if (error) {
    showToast(translateApiMessage(error.message) || t('posts.deleteError'));
    return;
  }

  showToast(t('posts.commentDeleted'));
  await renderThreadPanel(postId);
}

function startPostEdit(postId: string): void {
  const post = postsCache.get(postId);
  const bubble = document.querySelector(`[data-post-bubble="${postId}"]`);
  if (!post || !bubble) return;

  bubble.outerHTML = renderEditForm({
    kind: 'post',
    postId,
    content: post.content,
    maxLength: 1000,
  });
}

function startCommentEdit(postId: string, commentId: string): void {
  const comments = commentsCache.get(postId) ?? [];
  const comment = comments.find((item) => item.id === commentId);
  const bubble = document.querySelector(`[data-comment-bubble="${commentId}"]`);
  if (!comment || !bubble) return;

  bubble.outerHTML = renderEditForm({
    kind: 'comment',
    postId,
    commentId,
    content: comment.content,
    maxLength: 500,
    mine: true,
  });
}

async function submitEditForm(form: HTMLFormElement): Promise<void> {
  const kind = form.getAttribute('data-edit-form');
  const postId = form.getAttribute('data-post-id') ?? '';
  const commentId = form.getAttribute('data-comment-id') ?? undefined;
  const textarea = form.querySelector('textarea') as HTMLTextAreaElement | null;
  const content = textarea?.value.trim() ?? '';
  if (!postId || !content || !kind) return;

  const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;
  if (submitBtn) submitBtn.disabled = true;

  if (kind === 'post') {
    const { data, error } = await api.PATCH('/posts/{id}', {
      params: { path: { id: postId } },
      body: { content },
    });

    if (submitBtn) submitBtn.disabled = false;

    if (error) {
      showToast(translateApiMessage(error.message) || t('posts.editError'));
      return;
    }

    const updated = data as PostItem | undefined;
    if (updated) {
      postsCache.set(postId, updated);
      updatePostExcerptInFeed(postId, updated.content);
    }

    showToast(t('posts.postUpdated'));
    await renderThreadPanel(postId);
    return;
  }

  if (kind === 'comment' && commentId) {
    const { data, error } = await api.PATCH('/posts/{id}/comments/{commentId}', {
      params: { path: { id: postId, commentId } },
      body: { content },
    });

    if (submitBtn) submitBtn.disabled = false;

    if (error) {
      showToast(translateApiMessage(error.message) || t('posts.editError'));
      return;
    }

    const updated = data as CommentItem | undefined;
    if (updated) {
      const comments = commentsCache.get(postId) ?? [];
      commentsCache.set(
        postId,
        comments.map((item) => (item.id === commentId ? updated : item)),
      );
    }

    showToast(t('posts.commentUpdated'));
    await renderThreadPanel(postId);
  }
}

function bindThreadMessageActions(): void {
  const host = document.getElementById('thread-messages');
  if (!host || host.dataset.actionsBound) return;
  host.dataset.actionsBound = '1';

  host.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const editPostBtn = target.closest('[data-edit-post]') as HTMLElement | null;
    if (editPostBtn) {
      event.preventDefault();
      const postId = editPostBtn.getAttribute('data-edit-post');
      if (postId) startPostEdit(postId);
      return;
    }

    const deletePostBtn = target.closest('[data-delete-post]') as HTMLElement | null;
    if (deletePostBtn) {
      event.preventDefault();
      const postId = deletePostBtn.getAttribute('data-delete-post');
      if (postId) void deletePost(postId);
      return;
    }

    const editCommentBtn = target.closest('[data-edit-comment]') as HTMLElement | null;
    if (editCommentBtn) {
      event.preventDefault();
      const commentId = editCommentBtn.getAttribute('data-edit-comment');
      const postId = editCommentBtn.getAttribute('data-post-id');
      if (postId && commentId) startCommentEdit(postId, commentId);
      return;
    }

    const deleteCommentBtn = target.closest('[data-delete-comment]') as HTMLElement | null;
    if (deleteCommentBtn) {
      event.preventDefault();
      const commentId = deleteCommentBtn.getAttribute('data-delete-comment');
      const postId = deleteCommentBtn.getAttribute('data-post-id');
      if (postId && commentId) void deleteComment(postId, commentId);
      return;
    }

    const cancelBtn = target.closest('[data-cancel-edit]') as HTMLElement | null;
    if (cancelBtn && selectedPostId) {
      event.preventDefault();
      void renderThreadPanel(selectedPostId);
    }
  });

  host.addEventListener('submit', (event) => {
    const form = (event.target as HTMLElement).closest('[data-edit-form]') as HTMLFormElement | null;
    if (!form) return;
    event.preventDefault();
    void submitEditForm(form);
  });
}

function bindCommentForms() {
  document.querySelectorAll('.compose-pill[data-comment-form]').forEach((form) => {
    if (form.dataset.bound) return;
    form.dataset.bound = '1';

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const postId = form.getAttribute('data-comment-form');
      if (!postId) return;

      const errorEl = document.querySelector(`[data-comment-error="${postId}"]`);
      const contentInput = form.querySelector('[name="content"]') as HTMLTextAreaElement | null;
      const petInput =
        (document.getElementById(`pet-${postId}`) as HTMLSelectElement | null) ??
        (form.querySelector('[name="petId"]') as HTMLInputElement | HTMLSelectElement | null);
      const content = contentInput?.value.trim() ?? '';
      const petId = petInput?.value ?? '';
      if (!content) return;
      if (!petId) {
        showToast(t('posts.missingPetHint'));
        return;
      }

      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }

      const submitBtn = form.querySelector('.compose-pill__send') as HTMLButtonElement | null;
      if (submitBtn) submitBtn.disabled = true;

      const { error } = await api.POST('/posts/{id}/comments', {
        params: { path: { id: postId } },
        body: { content, petId },
      });

      if (submitBtn) submitBtn.disabled = false;

      if (error) {
        if (errorEl) {
          errorEl.textContent = translateApiMessage(error.message) ?? t('posts.commentError');
          errorEl.hidden = false;
        }
        return;
      }

      if (contentInput) contentInput.value = '';
      await renderThreadPanel(postId);
    });
  });
}

function bindThreadItems() {
  document.querySelectorAll('.thread-item').forEach((btn) => {
    if (btn.dataset.bound) return;
    btn.dataset.bound = '1';
    btn.addEventListener('click', () => {
      const postId = btn.getAttribute('data-thread-id');
      if (postId) selectThread(postId);
    });
  });
}

function cachePosts(items: PostItem[]): void {
  items.forEach((post) => postsCache.set(post.id, post));
}

function renderFeedList(items: PostItem[], reset: boolean): void {
  const list = document.getElementById('posts-list');
  if (!list) return;

  if (reset) {
    const rows = items.map(renderThreadItem);
    if (rows.length >= 2) rows.splice(2, 0, feedAdItemHtml());
    list.innerHTML = items.length
      ? rows.join('')
      : `<p class="posts-empty">${t('posts.empty')}</p>`;
  } else if (items.length) {
    const existingIds = new Set(
      [...list.querySelectorAll('.thread-item')].map((el) => el.getAttribute('data-thread-id')),
    );
    const newItems = items.filter((post) => !existingIds.has(post.id));
    if (newItems.length) {
      list.insertAdjacentHTML('beforeend', newItems.map(renderThreadItem).join(''));
    }
  }

  bindThreadItems();
  updateThreadSelectionUi();
  refreshCommunityIcons();
  fillAdsenseUnits();
}

async function maybeSelectThreadAfterFeed(items: PostItem[], reset: boolean): Promise<void> {
  if (!items.length && reset) {
    selectedPostId = null;
    syncThreadPanelVisibility();
    return;
  }

  const mobile = isMobileCommunity();
  const targetId =
    pendingSelectPostId ??
    (reset && !selectedPostId && !mobile ? items[0]?.id : selectedPostId);
  pendingSelectPostId = null;

  if (targetId && postsCache.has(targetId)) {
    await selectThread(targetId);
    return;
  }

  if (reset && items[0] && !mobile) {
    await selectThread(items[0].id);
    return;
  }

  if (mobile && reset && !targetId) {
    syncThreadPanelVisibility();
  }
}

async function refreshFeed(reset = true) {
  const list = document.getElementById('posts-list');
  const loadMore = document.getElementById('posts-load-more');
  if (!list) return;
  if (!reset && (!nextCursor || loadingPosts)) return;

  const requestId = ++feedRequestId;

  if (reset) {
    nextCursor = null;
    if (!selectedPostId) {
      list.innerHTML = `<p class="posts-empty">${t('posts.loading')}</p>`;
    }
  }

  loadingPosts = true;
  const query: { limit: number; cursor?: string } = { limit: 20 };
  if (nextCursor) query.cursor = nextCursor;

  const { data, error } = await api.GET('/posts', { params: { query } });
  loadingPosts = false;

  if (requestId !== feedRequestId) return;

  if (error) {
    list.innerHTML = `<p class="posts-error">${t('posts.loadError')}</p>`;
    if (loadMore) loadMore.hidden = true;
    return;
  }

  const items = (data?.items ?? []) as PostItem[];
  nextCursor = data?.nextCursor ?? null;
  cachePosts(items);
  renderFeedList(items, reset);

  if (loadMore) loadMore.hidden = !nextCursor;

  await maybeSelectThreadAfterFeed(items, reset);
}

async function handleCreatePost(e: Event) {
  e.preventDefault();
  const form = e.target as HTMLFormElement;
  const input = form.querySelector('[name="content"]') as HTMLTextAreaElement | null;
  const errorEl = document.getElementById('posts-create-error');
  const extras = document.getElementById('posts-create-extras');
  const select = document.getElementById('posts-headstone-select') as HTMLSelectElement | null;
  const content = input?.value.trim() ?? '';
  if (!content) return;

  let headstoneId = form.dataset.headstoneId || select?.value || '';
  const quota = getPostQuota().filter((q) => q.canPostThisWeek);

  if (quota.length > 1 && extras?.hidden) {
    extras.hidden = false;
    select?.focus();
    return;
  }

  if (!headstoneId && quota.length === 1) {
    headstoneId = quota[0].headstoneId;
  }

  if (!headstoneId) {
    showToast(t('posts.selectMemorialHint'));
    return;
  }

  const submitBtn = form.querySelector('.compose-pill__send') as HTMLButtonElement | null;
  if (submitBtn) submitBtn.disabled = true;
  if (errorEl) {
    errorEl.hidden = true;
    errorEl.textContent = '';
  }

  const { data, error } = await api.POST('/posts', {
    body: { content, headstoneId },
  });

  if (submitBtn) submitBtn.disabled = false;

  if (error) {
    const hint = document.getElementById('posts-quota-hint');
    const message = translateApiMessage(error.message) ?? t('posts.publishError');
    showToast(message);
    if (hint && !extras?.hidden) {
      hint.textContent = message;
      hint.classList.add('posts-error');
    } else if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
    return;
  }

  if (input) input.value = '';
  await refreshPostsAccess();
  updateCreateForm();

  const created = data as PostItem | undefined;
  if (created && created.content.length < POST_SHORT_CONTENT_THRESHOLD) {
    showToast(t('posts.publishedAddReply'));
  }
  pendingSelectPostId = created?.id ?? null;
  await refreshFeed(true);
}

function bindOnce(el: HTMLElement | null, handler: () => void) {
  if (!el || el.dataset.feedBound) return;
  el.dataset.feedBound = '1';
  el.addEventListener('click', handler);
}

function bindOnceSubmit(el: HTMLFormElement | null, handler: (e: Event) => void) {
  if (!el || el.dataset.feedBound) return;
  el.dataset.feedBound = '1';
  el.addEventListener('submit', handler);
}

export function bindPostsFeedEvents() {
  const loadMore = document.getElementById('posts-load-more');
  const createForm = document.getElementById('posts-create-form') as HTMLFormElement | null;
  const threadBack = document.getElementById('community-thread-back');

  bindOnce(loadMore, () => refreshFeed(false));
  bindOnceSubmit(createForm, handleCreatePost);
  bindOnce(threadBack, () => {
    if (!isMobileCommunity()) return;
    selectedPostId = null;
    updateThreadSelectionUi();
  });
}

export function initPostsFeed() {
  onPostsAccessChange(() => updateCreateForm());
  onPostsAccessChange(updateGuestNotice);
  onViewChange((view) => {
    if (view !== 'community') {
      document.body.classList.remove('discussion-sheet-open');
      unbindVisualViewportInsets();
    } else {
      syncThreadPanelVisibility();
    }
  });
  bindPostsFeedEvents();
  updateGuestNotice();
}

export function refreshPostsFeedOnLocale() {
  const presence = document.getElementById('community-presence');
  if (presence) presence.textContent = t('posts.presence');

  const emptyTitle = document.querySelector('.community-thread-panel__empty-title');
  if (emptyTitle) emptyTitle.textContent = t('posts.selectDiscussion');

  const emptyHint = document.querySelector('.community-thread-panel__empty-hint');
  if (emptyHint) emptyHint.textContent = t('posts.selectDiscussionHint');

  const title = document.querySelector('.community-thread-panel__title');
  if (title) title.textContent = t('posts.discussion');

  const contentInput = document.getElementById('posts-content-input') as HTMLTextAreaElement | null;
  if (contentInput) {
    contentInput.placeholder = selectedPostId
      ? t('posts.newMessageWithDiscussion')
      : t('posts.newMessage');
  }

  updateComposeContext();

  bindPostsFeedEvents();
  updateCreateForm();
  updateGuestNotice();

  if (getCurrentView() === 'community') {
    refreshFeed(true).catch(() => null);
  }
}

export async function openCommunityView(): Promise<void> {
  ensureCommunityView();
  syncThreadPanelVisibility();
  showView('community');
  bindPostsFeedEvents();
  updateCreateForm();
  updateGuestNotice();
  await refreshFeed(true);
}

export async function openCommunityForHeadstone(headstoneId: string): Promise<void> {
  await refreshPostsAccess();
  ensureCommunityView();
  syncThreadPanelVisibility();
  showView('community');
  bindPostsFeedEvents();
  updateCreateForm(headstoneId);
  updateGuestNotice();
  await refreshFeed(true);

  const extras = document.getElementById('posts-create-extras');
  if (extras) extras.hidden = false;

  const input = document.getElementById('posts-content-input') as HTMLTextAreaElement | null;
  input?.focus();
}

export async function openPostsFeedPanel(): Promise<void> {
  await openCommunityView();
}

async function loadFeedUntilPost(postId: string): Promise<boolean> {
  nextCursor = null;
  const list = document.getElementById('posts-list');
  if (!list) return false;

  list.innerHTML = `<p class="posts-empty">${t('posts.loading')}</p>`;

  const allItems: PostItem[] = [];
  let cursor: string | null = null;

  do {
    const query: { limit: number; cursor?: string } = { limit: 50 };
    if (cursor) query.cursor = cursor;

    const { data, error } = await api.GET('/posts', { params: { query } });
    if (error) {
      list.innerHTML = `<p class="posts-error">${t('posts.loadError')}</p>`;
      return false;
    }

    const items = (data?.items ?? []) as PostItem[];
    allItems.push(...items);
    cursor = data?.nextCursor ?? null;

    if (allItems.some((p) => p.id === postId)) {
      nextCursor = cursor;
      cachePosts(allItems);
      renderFeedList(allItems, true);
      const loadMore = document.getElementById('posts-load-more');
      if (loadMore) loadMore.hidden = !nextCursor;
      return true;
    }
  } while (cursor);

  return false;
}

export async function openPostById(postId: string): Promise<boolean> {
  ensureCommunityView();
  showView('community');
  bindPostsFeedEvents();
  updateCreateForm();
  updateGuestNotice();

  pendingSelectPostId = postId;
  const found = await loadFeedUntilPost(postId);
  if (!found) return false;

  await selectThread(postId, { scroll: true });
  return true;
}
